import { Link } from '@tanstack/react-router';
import { Flag, Lightbulb, Pencil } from 'lucide-react';
import { cn, MathText } from '@jeraisonne/ui';
import { AvatarCircle } from '@/components/avatar-circle';
import {
  QUESTION_REPORT_STATUS_LABELS,
  reportEditSearch,
} from '@/lib/exercise-v2/question-reports';
import { formatRelativeDate } from './format';
import type { QuestionReportFeedRow } from './types';

/** En-tête selon qui a signalé (soi-même ou un autre compte). PUR. */
export function questionReportHeadline(
  data: Pick<QuestionReportFeedRow, 'reporter_id' | 'reporter' | 'kind'>,
  viewerId: string | null | undefined,
): { name: string; verb: string } {
  const self = !!viewerId && viewerId === data.reporter_id;
  const name = self ? 'Tu' : data.reporter?.display_name?.trim().split(' ')[0] || 'Un utilisateur';
  const verb =
    data.kind === 'improvement'
      ? self
        ? 'as proposé une amélioration'
        : 'propose une amélioration'
      : self
        ? 'as signalé une question'
        : 'a signalé une question';
  return { name, verb };
}

const STATUS_TONE: Record<QuestionReportFeedRow['status'], string> = {
  open: 'bg-amber-100 text-amber-800 ocean:bg-amber-500/20 ocean:text-amber-200',
  resolved: 'bg-green-100 text-green-700 ocean:bg-green-500/20 ocean:text-green-300',
  dismissed: 'bg-ink-100 text-ink-500 ocean:bg-white/10',
};

/**
 * Carte du Feed « X a signalé une question » (migration 0348) — admins et
 * auteur de l'exercice : le texte du signalement, puis « Modifier la question »
 * (éditeur ouvert sur elle) ou « Revoir l'exercice » (éditeur complet).
 */
export function QuestionReportCard({
  data,
  viewerId,
}: {
  data: QuestionReportFeedRow;
  viewerId?: string | null;
}) {
  const head = questionReportHeadline(data, viewerId);
  const search = reportEditSearch(data);
  const Icon = data.kind === 'improvement' ? Lightbulb : Flag;
  return (
    <div className="border-ink-100 ocean:bg-white/5 space-y-3 rounded-2xl border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <AvatarCircle
          name={data.reporter?.display_name ?? head.name}
          src={data.reporter?.avatar_url ?? null}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <p className="text-primary-600 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider">
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {data.kind === 'improvement' ? 'Amélioration' : 'Signalement'}
          </p>
          <p className="text-ink-700 text-sm">
            <span className="text-ink-900 font-semibold">{head.name}</span> {head.verb}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-ink-400 text-xs">{formatRelativeDate(data.created_at)}</span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-semibold',
              STATUS_TONE[data.status],
            )}
          >
            {QUESTION_REPORT_STATUS_LABELS[data.status]}
          </span>
        </div>
      </div>

      <div className="space-y-1">
        <p className="text-ink-900 truncate text-sm font-semibold">
          <MathText>{data.exercises?.title ?? 'Exercice'}</MathText>
        </p>
        {data.question_excerpt && (
          <p className="text-ink-500 line-clamp-2 text-xs">
            <MathText>{data.question_excerpt}</MathText>
          </p>
        )}
      </div>

      <blockquote className="border-primary-300 bg-primary-50/60 ocean:bg-primary-500/10 text-ink-800 whitespace-pre-line rounded-r-xl border-l-4 px-3 py-2 text-sm">
        {data.message}
      </blockquote>

      <div className="flex flex-col gap-2 sm:flex-row">
        {data.question_block_id && (
          <Link
            to="/tutor/exercises/$exerciseId/edit-v2"
            params={{ exerciseId: data.exercise_id }}
            search={search}
            className="bg-primary-500 flex min-h-[2.75rem] flex-1 items-center justify-center gap-1.5 rounded-full px-4 text-sm font-semibold text-white active:scale-[0.98]"
          >
            <Pencil className="h-4 w-4" aria-hidden /> Modifier la question
          </Link>
        )}
        <Link
          to="/tutor/exercises/$exerciseId/edit-v2"
          params={{ exerciseId: data.exercise_id }}
          search={{ report: data.id }}
          className="border-ink-200 text-ink-700 flex min-h-[2.75rem] flex-1 items-center justify-center rounded-full border bg-white px-4 text-sm font-semibold active:scale-[0.98]"
        >
          Revoir l’exercice
        </Link>
      </div>
    </div>
  );
}
